import * as types from 'redux/constants/actionTypes';
import { getSelectedCustomerId } from 'selectors/customerSelectors';
import { addErrorNotification, addSuccessNotification } from 'redux/actions/notificationActions';

/**
 * @param {int} customerId
 * @param {moment} [startDate]
 * @param {moment} [endDate]
 */
export function fetchCustomerDashboardData(customerId, startDate, endDate) {
  return (dispatch, getState, { APIFactory }) => {
    dispatch({ type: types.CUSTOMER_DASHBOARD_FETCHING, payload: { customerId } });

    let apiUrl;
    if (startDate) {
      apiUrl = `/report/customer/${customerId}?startDate=${startDate.format()}&endDate=${endDate.format()}`;
    } else {
      apiUrl = `/report/customer/${customerId}`;
    }

    const api = APIFactory.getInstance(getState());
    return api
      .get(apiUrl)
      .then(response => {
        dispatch({
          type: types.CUSTOMER_DASHBOARD_FETCHED,
          payload: { customerId, data: response.data },
        });
      })
      .catch(response => {
        dispatch({
          type: types.CUSTOMER_DASHBOARD_FETCH_ERROR,
          payload: { customerId, response },
        });
      });
  };
}

/**
 * @param {int} locationId
 * @param {moment} [startDate]
 * @param {moment} [endDate]
 */
export function fetchLocationDashboardData(locationId, startDate, endDate) {
  return (dispatch, getState, { APIFactory }) => {
    dispatch({ type: types.LOCATION_DASHBOARD_FETCHING, payload: { locationId } });

    let apiUrl;
    if (startDate) {
      apiUrl = `/report/location/${locationId}?startDate=${startDate.format()}&endDate=${endDate.format()}`;
    } else {
      apiUrl = `/report/location/${locationId}`;
    }

    const api = APIFactory.getInstance(getState());
    return api
      .get(apiUrl)
      .then(response => {
        dispatch({
          type: types.LOCATION_DASHBOARD_FETCHED,
          payload: { locationId, data: response.data },
        });
      })
      .catch(response => {
        dispatch({
          type: types.LOCATION_DASHBOARD_FETCH_ERROR,
          payload: { locationId, response },
        });
      });
  };
}

/**
 * @param {int} teamId
 * @param {moment} [startDate]
 * @param {moment} [endDate]
 */
export function fetchTeamDashboardData(teamId, startDate, endDate) {
  return (dispatch, getState, { APIFactory }) => {
    dispatch({ type: types.TEAM_DASHBOARD_FETCHING, payload: { teamId } });

    let apiUrl;
    if (startDate) {
      apiUrl = `/report/team/${teamId}?startDate=${startDate.format()}&endDate=${endDate.format()}`;
    } else {
      apiUrl = `/report/team/${teamId}`;
    }

    const api = APIFactory.getInstance(getState());
    return api
      .get(apiUrl)
      .then(response => {
        dispatch({
          type: types.TEAM_DASHBOARD_FETCHED,
          payload: { teamId, data: response.data },
        });
      })
      .catch(response => {
        dispatch({ type: types.TEAM_DASHBOARD_FETCH_ERROR, payload: { teamId, response } });
      });
  };
}

/**
 * @param {int} customerId
 * @param {moment} startDate
 * @param {moment} endDate
 */
export function fetchCustomerSummaryData(customerId, startDate, endDate) {
  return (dispatch, getState, { APIFactory }) => {
    dispatch({ type: types.CUSTOMER_SUMMARY_FETCHING, payload: { customerId } });
    const api = APIFactory.getInstance(getState());
    return api
      .get(`/report/summary/${customerId}`, {
        params: { startDate: startDate.format(), endDate: endDate.format() },
        timeout: 60000,
      })
      .then(response => {
        dispatch({
          type: types.CUSTOMER_SUMMARY_FETCHED,
          payload: { customerId, data: response.data },
        });
      })
      .catch(response => {
        dispatch({
          type: types.CUSTOMER_SUMMARY_FETCH_ERROR,
          payload: { customerId, response },
        });
      });
  };
}

/**
 * @param {int} locationId
 * @param {moment} startDate
 * @param {moment} endDate
 */
export function fetchLocationSummaryData(locationId, startDate, endDate) {
  return (dispatch, getState, { APIFactory }) => {
    dispatch({ type: types.LOCATION_SUMMARY_FETCHING, payload: { locationId } });
    const api = APIFactory.getInstance(getState());
    return api
      .get(`/report/summary/location/${locationId}`, {
        params: { startDate: startDate.format(), endDate: endDate.format() },
        timeout: 60000,
      })
      .then(response => {
        dispatch({
          type: types.LOCATION_SUMMARY_FETCHED,
          payload: { locationId, data: response.data },
        });
      })
      .catch(response => {
        dispatch({
          type: types.LOCATION_SUMMARY_FETCH_ERROR,
          payload: { locationId, response },
        });
      });
  };
}

/**
 * @param {int} locationId
 * @param {moment} startDate
 * @param {moment} endDate
 */
export function fetchTeamSummaryData(teamId, startDate, endDate) {
  return (dispatch, getState, { APIFactory }) => {
    dispatch({ type: types.TEAM_SUMMARY_FETCHING, payload: { teamId } });
    const api = APIFactory.getInstance(getState());
    return api
      .get(`/report/summary/team/${teamId}`, {
        params: { startDate: startDate.format(), endDate: endDate.format() },
        timeout: 60000,
      })
      .then(response => {
        dispatch({
          type: types.TEAM_SUMMARY_FETCHED,
          payload: { teamId, data: response.data },
        });
      })
      .catch(response => {
        dispatch({
          type: types.TEAM_SUMMARY_FETCH_ERROR,
          payload: { teamId, response },
        });
      });
  };
}

/**
 * @param {int} userId
 * @param {int} customerId
 * @param {moment} startDate
 * @param {moment} endDate
 */
export function fetchAgentSummaryData(userId, customerId, startDate, endDate) {
  return (dispatch, getState, { APIFactory }) => {
    dispatch({ type: types.AGENT_SUMMARY_FETCHING, payload: { userId } });
    const api = APIFactory.getInstance(getState());
    return api
      .get(`/report/agent/${userId}/${customerId}`, {
        params: { startDate: startDate.format(), endDate: endDate.format() },
        timeout: 60000,
      })
      .then(response => {
        dispatch({
          type: types.AGENT_SUMMARY_FETCHED,
          payload: { userId, data: response.data },
        });
      })
      .catch(response => {
        dispatch({ type: types.AGENT_SUMMARY_FETCH_ERROR, payload: { userId, response } });
      });
  };
}

/**
 * @param {moment} startDate
 * @param {moment} endDate
 * @param {int} locationId
 * @param {int} page
 */
export function fetchConversationHistoryReportData(
  startDate,
  endDate,
  locationId,
  agentUserId,
  page = 1
) {
  return (dispatch, getState, { APIFactory }) => {
    const customerId = getSelectedCustomerId(getState());
    dispatch({ type: types.CONVO_HISTORY_REPORT_FETCHING, payload: { customerId } });
    const api = APIFactory.getInstance(getState());
    const payload = {
      customerId,
      page,
      perPage: 50,
      startDate: startDate.format(),
      endDate: endDate.format(),
    };
    if (locationId) {
      payload.locationId = locationId;
    }
    if (agentUserId) {
      payload.agentUserId = agentUserId;
    }

    return api
      .post('/report/leads/run', payload, { timeout: 60000 })
      .then(response => {
        dispatch({
          type: types.CONVO_HISTORY_REPORT_FETCHED,
          payload: { customerId, startDate, endDate, page, data: response.data },
        });
      })
      .catch(response => {
        dispatch({
          type: types.CONVO_HISTORY_REPORT_FETCH_ERROR,
          payload: { customerId, response },
        });
      });
  };
}

/**
 * @param {moment} startDate
 * @param {moment} endDate
 * @param {int} locationId
 * @param {string} [query]
 * @param {int} [page=1]
 */
export function fetchCrmAuditLogReportData(startDate, endDate, locationId, query, page = 1) {
  return (dispatch, getState, { APIFactory }) => {
    const customerId = getSelectedCustomerId(getState());
    dispatch({ type: types.CRM_AUDIT_REQUESTED });
    const api = APIFactory.getInstance(getState());
    const payload = {
      customerId,
      page,
      query,
      perPage: 50,
      startDate: startDate.format(),
      endDate: endDate.format(),
    };
    if (locationId) {
      payload.locationId = locationId;
    }

    return api
      .post('/report/crm-audit-log', payload)
      .then(response => {
        dispatch({ type: types.CRM_AUDIT_COMPLETED });
        return response.data;
      })
      .catch(error => {
        dispatch({ type: types.CRM_AUDIT_FAILED });
        let errorMessage;
        if (error || !error.response) {
          errorMessage = 'Unknown Error';
        } else {
          errorMessage =
              error.response.data && error.response.data.errors
                ? error.response.data.errors.join('. ')
                : error.response.statusText;
        }
        dispatch(
          addErrorNotification(
            `There was an error fetching the CRM Audit Log. (${errorMessage})`
          )
        );
        return {};
      });
  };
}

/**
 * @param {moment} startDate
 * @param {moment} endDate
 * @param {int} locationId
 * @param {string} [query]
 * @param {int} [page=1]
 */
export function fetchLeadPlatformLogData(startDate, endDate, locationId, query, page = 1) {
  return (dispatch, getState, { APIFactory }) => {
    const customerId = getSelectedCustomerId(getState());
    dispatch({ type: types.LEAD_PLATFORM_LOG_REQUESTED });
    const api = APIFactory.getInstance(getState());
    const payload = {
      customerId,
      page,
      query,
      perPage: 50,
      startDate: startDate.format(),
      endDate: endDate.format(),
    };
    if (locationId) {
      payload.locationId = locationId;
    }
    return api
      .post('/report/lead-platform-log', payload)
      .then(response => {
        dispatch({ type: types.LEAD_PLATFORM_LOG_COMPLETED });
        return response.data;
      })
      .catch(error => {
        dispatch({ type: types.LEAD_PLATFORM_LOG_FAILED });
        let errorMessage;
        if (!error || !error.response) {
          errorMessage = 'Unknown Error';
        } else {
          errorMessage =
            error.response.data && error.response.data.errors
              ? error.response.data.errors.join('. ')
              : error.response.statusText;
        }
        dispatch(
          addErrorNotification(
            `There was an error fetching the Lead Platform Log. (${errorMessage})`
          )
        );
        return {};
      });
  };
}

/**
 * @param {int} id
 */
export function fetchLeadPlatformLogDetail(id) {
  return (dispatch, getState, { APIFactory }) => {
    dispatch({ type: types.LEAD_PLATFORM_LOG_DETAIL_REQUESTED });
    const api = APIFactory.getInstance(getState());
    return api
      .get(`/report/lead-platform-log/${id}`)
      .then(response => {
        dispatch({ type: types.LEAD_PLATFORM_LOG_DETAIL_COMPLETED });
        return response.data;
      })
      .catch(() => {
        dispatch({ type: types.LEAD_PLATFORM_LOG_DETAIL_FAILED });
        dispatch(addErrorNotification('There was an error fetching the lead detail.'));
        return null;
      });
  };
}

export function fetchTwilioAuditReportData() {
  return (dispatch, getState, { APIFactory }) => {
    dispatch({ type: types.TWILIO_AUDIT_REQUESTED });
    const api = APIFactory.getInstance(getState());
    return api
      .get('/report/twilio-audit')
      .then(response => {
        dispatch({ type: types.TWILIO_AUDIT_COMPLETED });
        return response.data;
      })
      .catch(() => {
        dispatch({ type: types.TWILIO_AUDIT_FAILED });
        dispatch(addErrorNotification('There was an error fetching the Twilio audit report.'));
        return null;
      });
  };
}

/**
 * One-click fix for the "Twilio set up, Send to Mobile never enabled" audit indicator.
 * @param {number} locationId
 */
export function enableSendToMobile(locationId) {
  return (dispatch, getState, { APIFactory }) => {
    const api = APIFactory.getInstance(getState());
    return api
      .post(`/report/twilio-audit/${locationId}/enable-send-to-mobile`)
      .then(() => {
        dispatch(addSuccessNotification('Send to Mobile has been enabled for this location.'));
        return true;
      })
      .catch(() => {
        dispatch(addErrorNotification('There was an error enabling Send to Mobile for this location.'));
        return false;
      });
  };
}

/**
 * One-click fix for the "missing Twilio setup columns" audit indicator — checks
 * Twilio for a terminal profile/brand status that hasn't made it back to the
 * location yet, and fills in whichever of the two is currently empty.
 * @param {number} locationId
 */
export function fixMissingTwilioSetup(locationId) {
  return (dispatch, getState, { APIFactory }) => {
    const api = APIFactory.getInstance(getState());
    return api
      .post(`/report/twilio-audit/${locationId}/fix-missing-twilio-setup`)
      .then(response => {
        dispatch(addSuccessNotification('Checked Twilio for updated profile/brand status.'));
        return response.data;
      })
      .catch(() => {
        dispatch(addErrorNotification('There was an error checking Twilio for updated setup status.'));
        return null;
      });
  };
}

// Fix It: re-fetches a campaign with a placeholder rejection reason from Twilio.
export function refetchCampaignReason(locationId) {
  return (dispatch, getState, { APIFactory }) => {
    const api = APIFactory.getInstance(getState());
    return api
      .post(`/report/twilio-audit/${locationId}/refetch-campaign-reason`)
      .then(response => {
        dispatch(
          addSuccessNotification(
            response.data.campaignReasonChanged
              ? 'Updated the campaign from Twilio.'
              : 'Checked Twilio — no newer campaign rejection reason is available yet.'
          )
        );
        return response.data;
      })
      .catch(() => {
        dispatch(addErrorNotification('There was an error checking Twilio for an updated campaign reason.'));
        return null;
      });
  };
}

/**
 * One-click fix for a rejected profile/brand/campaign on a location that's already
 * been deleted — clears the location's Twilio data so it drops off this report.
 * @param {number} locationId
 */
export function clearTwilioData(locationId) {
  return (dispatch, getState, { APIFactory }) => {
    const api = APIFactory.getInstance(getState());
    return api
      .post(`/report/twilio-audit/${locationId}/clear-twilio-data`)
      .then(() => {
        dispatch(addSuccessNotification('Twilio data has been cleared for this location.'));
        return true;
      })
      .catch(() => {
        dispatch(addErrorNotification('There was an error clearing Twilio data for this location.'));
        return false;
      });
  };
}

export function releaseTwilioNumber(sid) {
  return (dispatch, getState, { APIFactory }) => {
    const api = APIFactory.getInstance(getState());
    return api
      .post(`/report/twilio-audit/${sid}/release-number`)
      .then(() => {
        dispatch(addSuccessNotification('The phone number has been released in Twilio.'));
        return true;
      })
      .catch(() => {
        dispatch(addErrorNotification('There was an error releasing the phone number in Twilio.'));
        return false;
      });
  };
}

/**
 * One-click fix for the "failed-no-reason-captured" audit indicator — re-fetches
 * the profile/brand from Twilio to recover a failure reason that was never captured.
 * @param {number} locationId
 */
export function fixMissingFailureReason(locationId) {
  return (dispatch, getState, { APIFactory }) => {
    const api = APIFactory.getInstance(getState());
    return api
      .post(`/report/twilio-audit/${locationId}/fix-missing-failure-reason`)
      .then(response => {
        dispatch(addSuccessNotification('Checked Twilio for the missing failure reason.'));
        return response.data;
      })
      .catch(() => {
        dispatch(addErrorNotification('There was an error checking Twilio for the missing failure reason.'));
        return null;
      });
  };
}

/**
 * One-click fix for the "campaign-complete-no-number" audit indicator — purchases
 * (or recovers) a phone number and assigns it to the location's Messaging Service.
 * @param {number} locationId
 */
export function assignNumber(locationId) {
  return (dispatch, getState, { APIFactory }) => {
    const api = APIFactory.getInstance(getState());
    return api
      .post(`/report/twilio-audit/${locationId}/assign-number`)
      .then(() => {
        dispatch(addSuccessNotification('A phone number has been purchased and assigned to this location.'));
        return true;
      })
      .catch(() => {
        dispatch(addErrorNotification('There was an error assigning a phone number to this location.'));
        return false;
      });
  };
}

// status: 'active' | 'deleted' | 'all' — filtered server-side since most locations are deleted.
export function fetchLocationDirectoryReportData(status) {
  return (dispatch, getState, { APIFactory }) => {
    dispatch({ type: types.LOCATION_DIRECTORY_REQUESTED });
    const api = APIFactory.getInstance(getState());
    return api
      .get('/report/location-directory', { params: { status } })
      .then(response => {
        dispatch({ type: types.LOCATION_DIRECTORY_COMPLETED });
        return response.data;
      })
      .catch(() => {
        dispatch({ type: types.LOCATION_DIRECTORY_FAILED });
        dispatch(addErrorNotification('There was an error fetching the location directory.'));
        return null;
      });
  };
}

// Location Directory row actions all resolve to the refreshed row, or null on failure.
function locationDirectoryAction(path, successMessage, errorMessage, body) {
  return (dispatch, getState, { APIFactory }) => {
    const api = APIFactory.getInstance(getState());
    return api
      .post(`/report/location-directory/${path}`, body)
      .then(response => {
        dispatch(addSuccessNotification(successMessage));
        return response.data.item;
      })
      .catch(error => {
        // 4xx responses carry the backend's reason (e.g. CCID already in use); show it when present.
        const status = error?.response?.status;
        const reason = status >= 400 && status < 500 ? error.response.data?.message : null;
        dispatch(addErrorNotification(reason ? `${errorMessage} (${reason})` : errorMessage));
        return null;
      });
  };
}

export function enableLocationDirectorySendToMobile(locationId) {
  return locationDirectoryAction(
    `${locationId}/enable-send-to-mobile`,
    'Send to Mobile has been enabled for this location.',
    'There was an error enabling Send to Mobile for this location.'
  );
}

export function fixLocationDirectoryTwilioSetup(locationId) {
  return locationDirectoryAction(
    `${locationId}/fix-missing-twilio-setup`,
    'Checked Twilio for updated profile/brand status.',
    'There was an error checking Twilio for updated setup status.'
  );
}

export function clearLocationDirectoryTwilioData(locationId) {
  return locationDirectoryAction(
    `${locationId}/clear-twilio-data`,
    'Twilio data has been cleared for this location.',
    'There was an error clearing Twilio data for this location.'
  );
}

export function reactivateLocationDirectoryLocation(locationId) {
  return locationDirectoryAction(
    `${locationId}/reactivate`,
    'The location has been reinstated.',
    'There was an error reinstating this location. Check that no active location shares its CCID.'
  );
}

export function duplicateLocationDirectoryLocation(locationId) {
  return locationDirectoryAction(
    `${locationId}/duplicate`,
    'The location has been duplicated.',
    'There was an error duplicating this location.'
  );
}

/**
 * @param {moment} startDate
 * @param {moment} endDate
 * @param {int} locationId
 * @param {int} page
 */
export function downloadConversationHistoryReport(startDate, endDate, locationId) {
  return (dispatch, getState, { APIFactory }) => {
    const customerId = getSelectedCustomerId(getState());
    dispatch({ type: types.CONVO_HISTORY_REPORT_CSV_FETCHING, payload: { customerId } });
    const api = APIFactory.getInstance(getState());
    const payload = {
      customerId,
      startDate: startDate.format(),
      endDate: endDate.format(),
    };
    if (locationId) {
      payload.locationId = locationId;
    }

    return api
      .post('/report/leads/download', payload, { timeout: 60000 })
      .then(response => {
        dispatch({
          type: types.CONVO_HISTORY_REPORT_CSV_FETCHED,
          payload: { customerId, startDate, endDate, data: response.data },
        });

        const a = document.createElement('a');
        document.body.appendChild(a); // Required in Firefox
        a.target = '_self'; // Required in Firefox
        a.textContent = 'download';
        a.download = 'conversations.csv';
        a.href = response.data.url;
        a.click();
        a.remove(); // Removes node from the DOM
      })
      .catch(response => {
        dispatch({
          type: types.CONVO_HISTORY_REPORT_CSV_FETCH_ERROR,
          payload: { customerId, response },
        });
      });
  };
}

/**
 * @param {int} customerId
 * @param {moment} startDate
 * @param {moment} endDate
 */
export function fetchSoldCustomersSummary(customerId, startDate, endDate) {
  return (dispatch, getState, { APIFactory }) => {
    dispatch({ type: types.SOLD_CUSTOMERS_SUMMARY_FETCHING, payload: { customerId } });
    const api = APIFactory.getInstance(getState());
    return api
      .get(`/report/matchback/customer/${customerId}/summary`, {
        params: { startDate: startDate.format(), endDate: endDate.format() },
      })
      .then(response => {
        dispatch({
          type: types.SOLD_CUSTOMERS_SUMMARY_FETCHED,
          payload: { customerId, response },
        });
        if (response.status === 204) return {};
        return response.data;
      })
      .catch(response => {
        dispatch({
          type: types.SOLD_CUSTOMERS_SUMMARY_FETCH_ERROR,
          payload: { customerId, response },
        });
        dispatch(addErrorNotification('Error fetching customer summary'));
        return {};
      });
  };
}

/**
 * @param {int} customerId
 * @param {moment} startDate
 * @param {moment} endDate
 */
export function fetchSoldCustomersDetails(customerId, startDate, endDate) {
  return (dispatch, getState, { APIFactory }) => {
    dispatch({ type: types.SOLD_CUSTOMERS_DETAILS_FETCHING, payload: { customerId } });
    const api = APIFactory.getInstance(getState());
    return api
      .get(`/report/matchback/customer/${customerId}/detail`, {
        params: { startDate: startDate.format(), endDate: endDate.format() },
      })
      .then(response => {
        dispatch({
          type: types.SOLD_CUSTOMERS_DETAILS_FETCHED,
          payload: { customerId, response },
        });
        if (response.status === 204) return [];
        return response.data;
      })
      .catch(response => {
        dispatch({
          type: types.SOLD_CUSTOMERS_DETAILS_FETCH_ERROR,
          payload: { customerId, response },
        });
        dispatch(addErrorNotification('Error fetching customer details'));
        return [];
      });
  };
}

/**
 * @param {int} locationId
 * @param {moment} startDate
 * @param {moment} endDate
 */
export function fetchSoldCustomersLocationSummary(locationId, startDate, endDate) {
  return (dispatch, getState, { APIFactory }) => {
    dispatch({ type: types.SOLD_CUSTOMERS_LOCATION_SUMMARY_FETCHING, payload: { locationId } });
    const api = APIFactory.getInstance(getState());
    return api
      .get(`/report/matchback/location/${locationId}/summary`, {
        params: { startDate: startDate.format(), endDate: endDate.format() },
      })
      .then(response => {
        dispatch({
          type: types.SOLD_CUSTOMERS_LOCATION_SUMMARY_FETCHED,
          payload: { locationId, response },
        });
        if (response.status === 204) return {};
        return response.data;
      })
      .catch(response => {
        dispatch({
          type: types.SOLD_CUSTOMERS_LOCATION_SUMMARY_FETCH_ERROR,
          payload: { locationId, response },
        });
        dispatch(addErrorNotification('Error fetching location summary'));
        return {};
      });
  };
}

/**
 * @param {int} locationId
 * @param {moment} startDate
 * @param {moment} endDate
 */
export function fetchSoldCustomersLocationDetails(locationId, startDate, endDate) {
  return (dispatch, getState, { APIFactory }) => {
    dispatch({ type: types.SOLD_CUSTOMERS_LOCATION_DETAILS_FETCHING, payload: { locationId } });
    const api = APIFactory.getInstance(getState());
    return api
      .get(`/report/matchback/location/${locationId}/detail`, {
        params: { startDate: startDate.format(), endDate: endDate.format() },
      })
      .then(response => {
        dispatch({
          type: types.SOLD_CUSTOMERS_LOCATION_DETAILS_FETCHED,
          payload: { locationId, response },
        });
        if (response.status === 204) return [];
        return response.data;
      })
      .catch(response => {
        dispatch({
          type: types.SOLD_CUSTOMERS_LOCATION_DETAILS_FETCH_ERROR,
          payload: { locationId, response },
        });
        dispatch(addErrorNotification('Error fetching location details'));
        return [];
      });
  };
}

export function sendDmsDataRequest(data) {
  return (dispatch, getState, { APIFactory }) => {
    const api = APIFactory.getInstance(getState());
    return api.post('/admin/email', data);
  };
}

export function fetchCallCenterBillingReport() {
  return (dispatch, getState, { APIFactory }) => {
    const api = APIFactory.getInstance(getState());
    const customerId = getSelectedCustomerId(getState());
    return api.get(`/callcenter/billing/customer/${customerId}`);
  };
}
